import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api, AED_short, AED } from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, RefreshCcw, MapPin, Clock, Gavel, Sparkles, X, ExternalLink,
  Search, Loader2, ArrowUpRight, ShieldAlert, ThumbsUp, TrendingUp, ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import DataFreshnessBanner from '../components/DataFreshnessBanner';

// ---------- Countdown ----------
function timeLeft(iso) {
  if (!iso) return { text: '—', urgent: false, ended: false };
  const end = new Date(iso).getTime();
  const now = Date.now();
  const ms = end - now;
  if (ms <= 0) return { text: 'Ended', urgent: false, ended: true };
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d >= 1) return { text: `${d}d ${h}h`, urgent: d < 2, ended: false };
  if (h >= 1) return { text: `${h}h ${m}m`, urgent: true, ended: false };
  return { text: `${m}m`, urgent: true, ended: false };
}

function useCountdown() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 30_000);
    return () => clearInterval(t);
  }, []);
}

// ---------- Filters ----------
const FILTERS = [
  { key: 'all',     label: 'Live' },
  { key: 'watched', label: 'Watchlist' },
  { key: 'ending',  label: 'Ending soon' },
  { key: 'new',     label: 'Just added' },
  { key: 'past',    label: 'Past' },
];

const EMIRATES = ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Umm Al Quwain', 'Fujairah'];

// ---------- Card ----------
function AuctionCard({ a, onWatch, onOpen }) {
  const tl = timeLeft(a.auction_end_at);
  const img = (a.images || [])[0];
  return (
    <motion.div
      layout
      whileHover={{ y: -3 }}
      onClick={() => onOpen(a)}
      data-testid={`auction-card-${a.external_id}`}
      className="cursor-pointer rounded-2xl overflow-hidden bg-white/85 border border-white/70 shadow-sm hover:shadow-lg transition-all"
    >
      <div className="relative h-44 bg-slate-200">
        {img && <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />}
        <button
          onClick={(e) => { e.stopPropagation(); onWatch(a); }}
          data-testid={`watch-${a.external_id}`}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 grid place-items-center shadow-md hover:scale-110 transition-transform"
          title={a.is_watched ? 'Remove from watchlist' : 'Add to watchlist'}
        >
          <Heart size={16} className={a.is_watched ? 'fill-red-500 text-red-500' : 'text-slate-600'} />
        </button>
        <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-widest ${
          tl.ended ? 'bg-slate-200 text-slate-500' : tl.urgent ? 'bg-red-500 text-white' : 'bg-slate-900/85 text-white'
        }`}>
          <Clock size={10} className="inline mr-1"/> {tl.text}
        </div>
        {a.is_physical && (
          <div className="absolute bottom-3 left-3 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest bg-amber-500/90 text-white">
            Physical
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-1 text-[11px] text-slate-500 mb-1">
          <MapPin size={11}/> {a.address || a.area || a.emirate || '—'}
        </div>
        <div className="font-medium text-slate-900 text-sm line-clamp-2 min-h-[2.5rem]">{a.title || 'Auction listing'}</div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div>
            <div className="text-slate-500">Current bid</div>
            <div className="font-semibold text-[#679286]">{AED_short(a.current_bid_aed)}</div>
          </div>
          <div>
            <div className="text-slate-500">Bids</div>
            <div className="font-medium">{a.bid_count || 0}</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ---------- Detail Drawer ----------
function DetailDrawer({ a, onClose, onWatch, onRefresh }) {
  const [analyzing, setAnalyzing] = useState(false);
  const [detail, setDetail] = useState(a);
  const [heroIdx, setHeroIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!a?.external_id) return;
    setHeroIdx(0);
    api.get(`/auctions/${a.external_id}`).then(r => setDetail(r.data)).catch(() => {});
  }, [a?.external_id]);

  // Keyboard: Esc to close lightbox / drawer, arrows to nav images
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { if (lightbox) setLightbox(false); else onClose(); }
      const imgs = detail.images || [];
      if (e.key === 'ArrowRight' && imgs.length) setHeroIdx(i => (i + 1) % imgs.length);
      if (e.key === 'ArrowLeft'  && imgs.length) setHeroIdx(i => (i - 1 + imgs.length) % imgs.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, detail.images, onClose]);

  const runAI = async () => {
    setAnalyzing(true);
    try {
      const r = await api.post(`/auctions/${a.external_id}/analyze`);
      setDetail(d => ({ ...d, ai_analysis: r.data.analysis }));
      toast.success('Iris AI analysis ready');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI analysis failed');
    } finally { setAnalyzing(false); }
  };

  const sendToDealAnalyzer = () => {
    // Apify's normalized `area` field actually contains a size-with-unit label (e.g. "1,538.49 ft²").
    // The neighborhood is in `address`. Extract numeric sqft from the size label if possible.
    const sizeSqft = typeof detail.size_sqft === 'number'
      ? detail.size_sqft
      : parseFloat(String(detail.area || '').replace(/[^\d.]/g, '')) || '';
    const q = new URLSearchParams({
      title: detail.title || '',
      community: detail.address || detail.emirate || '',
      building: detail.title || detail.subtitle || '',
      size_sqft: sizeSqft + '',
      bedrooms: detail.bedrooms || 0,
      bathrooms: detail.bathrooms || 0,
      asking_price_aed: detail.current_bid_aed || detail.starting_bid_aed || '',
      listing_url: detail.listing_url || '',
      notes: `From Emirates Auction #${detail.external_id}. Auction ends ${detail.auction_end_at || ''}`,
    });
    if (detail.latitude) q.set('lat', detail.latitude);
    if (detail.longitude) q.set('lng', detail.longitude);
    nav(`/app/deal-analyzer?${q.toString()}`);
  };

  if (!a) return null;
  const tl = timeLeft(detail.auction_end_at);
  const ai = detail.ai_analysis;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-end"
        data-testid="auction-detail-drawer"
      >
        <motion.div
          initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
          transition={{ type: 'tween', duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-2xl bg-gradient-to-b from-[#F5F1EB] via-[#EEE8DD] to-[#E9E5DE] shadow-2xl overflow-y-auto"
        >
          <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-xl border-b border-white/60 px-6 py-3 flex items-center gap-3">
            <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100" data-testid="close-auction-detail">
              <X size={18}/>
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Emirates Auction · #{detail.external_id}</div>
              <div className="font-serif text-lg truncate">{detail.title}</div>
            </div>
            <button onClick={() => onWatch(detail)} className="p-2 rounded-full hover:bg-slate-100" data-testid="detail-watch-toggle">
              <Heart size={18} className={detail.is_watched ? 'fill-red-500 text-red-500' : 'text-slate-500'} />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Interactive image gallery */}
            {(detail.images || []).length > 0 && (() => {
              const imgs = detail.images || [];
              return (
                <div>
                  <div
                    className="relative group rounded-xl overflow-hidden bg-slate-200 h-72 cursor-zoom-in"
                    onClick={() => setLightbox(true)}
                    data-testid="auction-hero-image"
                  >
                    <img src={imgs[heroIdx]} alt="" className="w-full h-full object-cover"/>
                    {imgs.length > 1 && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); setHeroIdx(i => (i - 1 + imgs.length) % imgs.length); }}
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 grid place-items-center shadow opacity-0 group-hover:opacity-100 transition"
                          aria-label="Previous image"
                        ><ChevronLeft size={18}/></button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setHeroIdx(i => (i + 1) % imgs.length); }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 grid place-items-center shadow opacity-0 group-hover:opacity-100 transition"
                          aria-label="Next image"
                        ><ChevronRight size={18}/></button>
                        <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-black/70 text-white text-[11px] font-semibold">
                          {heroIdx + 1} / {imgs.length}
                        </div>
                      </>
                    )}
                    <div className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full bg-black/70 text-white text-[10px] uppercase tracking-widest opacity-0 group-hover:opacity-100 transition">
                      Click to zoom
                    </div>
                  </div>
                  {imgs.length > 1 && (
                    <div className="mt-2 grid grid-cols-6 md:grid-cols-8 gap-2">
                      {imgs.map((src, i) => (
                        <button
                          key={i}
                          onClick={() => setHeroIdx(i)}
                          data-testid={`auction-thumb-${i}`}
                          className={`rounded-lg overflow-hidden aspect-square bg-slate-200 ring-2 transition ${
                            i === heroIdx ? 'ring-[#679286]' : 'ring-transparent hover:ring-white/70'
                          }`}
                        >
                          <img src={src} alt="" className="w-full h-full object-cover" loading="lazy"/>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Bid + Countdown */}
            <div className="grid grid-cols-3 gap-3">
              <div className="glass p-4 text-center">
                <div className="overline">Current bid</div>
                <div className="font-serif text-2xl text-[#679286]">{AED(detail.current_bid_aed)}</div>
                <div className="text-[11px] text-slate-500 mt-1">{detail.bid_count || 0} bids</div>
              </div>
              <div className="glass p-4 text-center">
                <div className="overline">Time left</div>
                <div className={`font-serif text-2xl ${tl.urgent ? 'text-red-500' : ''}`}>{tl.text}</div>
                <div className="text-[11px] text-slate-500 mt-1">{detail.auction_end_at?.slice(0, 16).replace('T', ' ')} UTC</div>
              </div>
              <div className="glass p-4 text-center">
                <div className="overline">Type</div>
                <div className="font-serif text-lg mt-1">{detail.property_type_code || '—'}</div>
                <div className="text-[11px] text-slate-500 mt-1">{detail.is_physical ? 'Physical auction' : 'Online'}</div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={runAI}
                disabled={analyzing}
                data-testid="auction-run-ai"
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#1A2530] text-white text-sm font-semibold hover:bg-[#0F1720] transition disabled:opacity-60"
              >
                {analyzing ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>}
                {ai ? 'Re-analyze with Iris AI' : 'Ask Iris AI'}
              </button>
              <button
                onClick={sendToDealAnalyzer}
                data-testid="auction-send-to-deal-analyzer"
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/80 border border-white/70 text-sm font-semibold hover:bg-white transition"
              >
                <TrendingUp size={14}/>
                Send to Deal Analyzer
              </button>
              {detail.listing_url && (
                <a
                  href={detail.listing_url} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/80 border border-white/70 text-sm font-semibold hover:bg-white transition"
                >
                  <ExternalLink size={14}/> View on Emirates Auction
                </a>
              )}
            </div>

            {/* AI Insight card */}
            {ai && <AIInsight ai={ai}/>}

            {/* Description + Details */}
            <div className="glass p-5">
              <div className="overline mb-2">Listing details</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-4">
                <Row k="Emirate" v={detail.emirate}/>
                <Row k="Address" v={detail.address}/>
                <Row k="Area" v={detail.area}/>
                <Row k="Property type" v={detail.property_type_code}/>
                <Row k="Bedrooms" v={detail.bedrooms || '—'}/>
                <Row k="Bathrooms" v={detail.bathrooms || '—'}/>
                <Row k="Starting bid" v={AED(detail.starting_bid_aed)}/>
                <Row k="Current bid" v={AED(detail.current_bid_aed)}/>
              </div>
              {detail.description && (
                <div className="mt-3 pt-3 border-t border-white/60 text-sm text-slate-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
                  {detail.description}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Fullscreen Lightbox */}
      {lightbox && (detail.images || []).length > 0 && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setLightbox(false)}
          className="fixed inset-0 z-[60] bg-black/95 grid place-items-center"
          data-testid="auction-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20"
            aria-label="Close"
          ><X size={20}/></button>
          {(detail.images || []).length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setHeroIdx(i => (i - 1 + detail.images.length) % detail.images.length); }}
                className="absolute left-6 w-12 h-12 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20"
                aria-label="Previous"
              ><ChevronLeft size={24}/></button>
              <button
                onClick={(e) => { e.stopPropagation(); setHeroIdx(i => (i + 1) % detail.images.length); }}
                className="absolute right-6 w-12 h-12 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20"
                aria-label="Next"
              ><ChevronRight size={24}/></button>
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-white/10 text-white text-sm">
                {heroIdx + 1} / {detail.images.length}
              </div>
            </>
          )}
          <img
            src={detail.images[heroIdx]} alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[88vh] object-contain shadow-2xl"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between gap-2"><span className="text-slate-500">{k}</span><span className="font-medium text-right truncate">{v ?? '—'}</span></div>;
}

function AIInsight({ ai }) {
  const recTint = {
    bid_aggressive: 'bg-emerald-500/10 text-emerald-700 border-emerald-300',
    bid_conservative: 'bg-blue-500/10 text-blue-700 border-blue-300',
    watch: 'bg-amber-500/10 text-amber-700 border-amber-300',
    skip: 'bg-red-500/10 text-red-700 border-red-300',
  }[ai.recommendation] || 'bg-slate-500/10 text-slate-700 border-slate-300';
  return (
    <div className="glass p-5 border-l-4 border-[#679286]" data-testid="auction-ai-insight">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={16} className="text-[#679286]"/>
        <div className="font-serif text-lg">Iris AI verdict</div>
        <div className={`ml-auto text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border ${recTint}`}>
          {(ai.recommendation || 'watch').replace('_', ' ')}
        </div>
      </div>
      <div className="text-sm text-slate-800 mb-3">{ai.verdict || ai.summary}</div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {ai.deal_score != null && <MetricPill label="Score" value={`${Math.round(ai.deal_score)}/100`} accent="text-[#679286]"/>}
        {ai.price_per_sqft_aed && <MetricPill label="AED/sqft" value={Math.round(ai.price_per_sqft_aed).toLocaleString()}/>}
        {ai.discount_pct_vs_portfolio != null && (
          <MetricPill label="vs portfolio" value={`${ai.discount_pct_vs_portfolio > 0 ? '+' : ''}${ai.discount_pct_vs_portfolio}%`}
            accent={ai.discount_pct_vs_portfolio < 0 ? 'text-emerald-600' : 'text-red-500'}/>
        )}
        {ai.projected_roi_pct != null && <MetricPill label="Proj. ROI" value={`${ai.projected_roi_pct}%`} accent="text-emerald-600"/>}
        {ai.refurb_budget_estimate_aed && <MetricPill label="Refurb est." value={AED_short(ai.refurb_budget_estimate_aed)}/>}
        {ai.projected_arv_aed && <MetricPill label="Proj. ARV" value={AED_short(ai.projected_arv_aed)}/>}
        {ai.max_bid_aed && <MetricPill label="Max bid" value={AED_short(ai.max_bid_aed)} accent="text-slate-900"/>}
        {ai.bid_velocity && <MetricPill label="Bid velocity" value={ai.bid_velocity.split('—')[0]?.trim()}/>}
      </div>

      {(ai.positives?.length || ai.risks?.length) ? (
        <div className="grid md:grid-cols-2 gap-4 pt-3 border-t border-white/60">
          {ai.positives?.length ? (
            <div>
              <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-2">
                <ThumbsUp size={12}/> Positives
              </div>
              <ul className="space-y-1 text-sm text-slate-700 list-disc pl-4">
                {ai.positives.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          ) : <div/>}
          {ai.risks?.length ? (
            <div>
              <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-red-500 mb-2">
                <ShieldAlert size={12}/> Risks
              </div>
              <ul className="space-y-1 text-sm text-slate-700 list-disc pl-4">
                {ai.risks.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          ) : <div/>}
        </div>
      ) : null}
    </div>
  );
}

function MetricPill({ label, value, accent }) {
  return (
    <div className="rounded-xl bg-white/70 border border-white/60 px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-sm font-semibold mt-0.5 ${accent || 'text-slate-800'}`}>{value}</div>
    </div>
  );
}

// ---------- Page ----------
export default function Auctions() {
  useCountdown();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [emirate, setEmirate] = useState('all');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/auctions');
      setList(r.data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Open deep-link from URL ?open=<id>
  useEffect(() => {
    const id = params.get('open');
    if (id && list.length) {
      const found = list.find(x => x.external_id === id);
      if (found) setOpen(found);
    }
  }, [params, list]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post('/auctions/refresh');
      toast.success(`Synced ${r.data.upserted} auctions`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally { setRefreshing(false); }
  };

  const toggleWatch = async (a) => {
    try {
      const r = await api.post(`/auctions/${a.external_id}/watch`);
      setList(xs => xs.map(x => x.external_id === a.external_id ? { ...x, is_watched: r.data.watched } : x));
      if (open?.external_id === a.external_id) setOpen(o => ({ ...o, is_watched: r.data.watched }));
      toast.success(r.data.watched ? 'Added to watchlist' : 'Removed from watchlist');
    } catch (e) { toast.error('Failed'); }
  };

  const filtered = useMemo(() => {
    let xs = list;
    const now = Date.now();
    // Hide ended auctions by default (clutter). Show them only when
    // user picks the 'past' filter explicitly.
    if (filter === 'past') {
      xs = xs.filter(x => new Date(x.auction_end_at).getTime() <= now);
      xs = [...xs].sort((a, b) =>
        new Date(b.auction_end_at).getTime() - new Date(a.auction_end_at).getTime()
      );
    } else {
      xs = xs.filter(x => new Date(x.auction_end_at).getTime() > now);
      if (filter === 'watched') xs = xs.filter(x => x.is_watched);
      if (filter === 'ending') xs = xs.filter(x => {
        const t = new Date(x.auction_end_at).getTime() - now;
        return t > 0 && t < 3 * 86400000;
      });
      if (filter === 'new') {
        const cutoff = now - 24 * 3600 * 1000;
        xs = xs.filter(x => new Date(x.fetched_at).getTime() >= cutoff);
      }
    }
    if (emirate !== 'all') xs = xs.filter(x => (x.emirate || '').toLowerCase() === emirate.toLowerCase());
    const s = q.trim().toLowerCase();
    if (s) xs = xs.filter(x =>
      (x.title + ' ' + (x.address || '') + ' ' + (x.area || '') + ' ' + (x.description || ''))
        .toLowerCase().includes(s)
    );
    return xs;
  }, [list, filter, emirate, q]);

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="auctions-page">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1A2530] to-[#282828] grid place-items-center shadow-lg">
          <Gavel className="text-white" size={22}/>
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">Market Intelligence</div>
          <h1 className="font-serif text-3xl">Emirates Auctions</h1>
          <p className="text-sm text-slate-500">Live Dubai auction listings · synced every 6 hours from Apify · {list.length} active</p>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          data-testid="auctions-refresh"
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/85 border border-white/70 text-sm hover:bg-white transition disabled:opacity-60"
        >
          {refreshing ? <Loader2 size={14} className="animate-spin"/> : <RefreshCcw size={14}/>}
          Refresh now
        </button>
      </div>

      {/* Data freshness warning (only renders when sync is stale) */}
      <div className="mb-6">
        <DataFreshnessBanner sources={['auctions']}/>
      </div>

      {/* Search + filters */}
      <div className="grid md:grid-cols-[1fr_auto_auto] gap-3 mb-6">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input
            value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search title, area, address, keywords…"
            data-testid="auctions-search"
            className="w-full pl-10 pr-3 py-2.5 rounded-full bg-white/85 border border-white/70 text-sm outline-none focus:ring-2 focus:ring-[#679286]/30"
          />
        </div>
        <select
          value={emirate} onChange={e => setEmirate(e.target.value)}
          data-testid="auctions-emirate"
          className="px-4 py-2.5 rounded-full bg-white/85 border border-white/70 text-sm outline-none"
        >
          <option value="all">All emirates</option>
          {EMIRATES.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <div className="flex gap-1 bg-white/70 rounded-full p-1 border border-white/60">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`auction-filter-${f.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                filter === f.key ? 'bg-[#1A2530] text-white' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {f.label} <span className="opacity-60">· {
                (() => {
                  const now = Date.now();
                  const live = list.filter(x => new Date(x.auction_end_at).getTime() > now);
                  if (f.key === 'all')     return live.length;
                  if (f.key === 'watched') return live.filter(x => x.is_watched).length;
                  if (f.key === 'ending')  return live.filter(x => {
                    const t = new Date(x.auction_end_at).getTime() - now;
                    return t > 0 && t < 3 * 86400000;
                  }).length;
                  if (f.key === 'new')     return live.filter(x => new Date(x.fetched_at).getTime() >= now - 86400000).length;
                  if (f.key === 'past')    return list.filter(x => new Date(x.auction_end_at).getTime() <= now).length;
                  return 0;
                })()
              }</span>
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => <div key={i} className="rounded-2xl bg-white/50 h-72 animate-pulse"/>)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="glass p-16 text-center text-slate-500">
          <Gavel size={32} className="mx-auto mb-3 opacity-40"/>
          <div className="font-serif text-lg">No auctions match</div>
          <div className="text-sm mt-1">Try clearing filters or hit "Refresh now" to fetch the latest.</div>
        </div>
      ) : (
        <motion.div layout className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="auctions-grid">
          {filtered.map(a => (
            <AuctionCard key={a.external_id} a={a} onWatch={toggleWatch} onOpen={setOpen}/>
          ))}
        </motion.div>
      )}

      {open && <DetailDrawer a={open} onClose={() => { setOpen(null); params.delete('open'); setParams(params); }} onWatch={toggleWatch} onRefresh={load}/>}
    </div>
  );
}
